import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Text, ScrollArea, Divider, Loader, Box, Button } from "@mantine/core";
import { useListState } from "@mantine/hooks";

import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import FilterSortableItem from "../presentations/filters/FilterSortableItem";
import FilterPropertySelector from "../presentations/filters/FilterPropertySelector";
import PremiumModal from "../../app/PremiumModal";

import SpaceMemberFilterModel from '../../../models/SpaceMemberFilters';
import SpaceFilterSettingModel from "../../../models/SpaceFilterSetting";

import { reorderOptions } from "../../../utils/Filters";

export default function Filters({ slug, type }) {
  const [allFilters, handlers] = useListState([]);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [canCreateFilters, setCanCreateFilters] = useState(false);
  const [opened, setOpened] = useState(false);

  const space = useSelector((state) => state.space);

  const FilterModel = type === "filter-members" ? SpaceMemberFilterModel : SpaceFilterSettingModel;
  const isComponent = ["filter-space"].includes(type);

  useEffect(() => {
    if (!slug) return;
    onFetchFilters();
  }, [slug]);

  useEffect(() => {
    if (space && space.permissions) {
      setCanCreateFilters(
        space.permissions.can_create_filters ||
        [space.permissions.can_activate_completions[0] || false, "can_create_filters"]
      );
    }
  }, [space]);

  const onFetchFilters = () => {
    FilterModel.onFetch(
      slug,
      (data) => {
        if (Array.isArray(data)) {
          handlers.setState(data);
        }
        setFiltersLoading(false);
      },
      (error) => {
        setFiltersLoading(false);
      }
    );
  }

  const handleReorder = (updatedItems) => {
    const order = updatedItems.map((item) => ({
      id: item.id,
      sort_order: item.sort_order
    }));
    if (!Array.isArray(order)) return;
    FilterModel.onReorder(
      slug,
      order,
      (data) => {},
      (error) => {}
    );
  };

  return (
    <>
      <ScrollArea spacing="xs" style={isComponent ? null : { height: "calc(100vh - 60px)" }}>
        {filtersLoading ? (
          <Box sx={{ width: "100%" }} align="center">
            <Loader size="sm" />
          </Box>
        ) : (
          <DndContext
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            onDragEnd={({ active, over }) => {
              if (active.id !== over?.id) {
                const oldIndex = allFilters.findIndex((i) => i.id === active.id);
                const newIndex = allFilters.findIndex((i) => i.id === over?.id);
                const updatedItems = reorderOptions(allFilters, oldIndex, newIndex);
                handlers.setState(updatedItems);
                
                handleReorder(updatedItems);
              }
            }}
            collisionDetection={closestCenter}
          >
            <SortableContext items={allFilters.map((i) => i.id)} strategy={verticalListSortingStrategy}>
              {allFilters.length ? allFilters.map((item) => (
                <FilterSortableItem
                  key={item.id}
                  id={item.id}
                  label={item.name}
                  emojicon={item.emojicon}
                  slug={slug}
                  type={type}
                  allFilters={allFilters}
                  handlers={handlers}
                  selectedProperty={selectedProperty?.id === item.id ? selectedProperty : null}
                  setSelectedProperty={setSelectedProperty}
                  onEdit={(shouldClose) => {
                    const newSelectedProperty = item.id === selectedProperty?.id ? null : item;
                    setSelectedProperty(null);
                    setTimeout(() => {
                      setSelectedProperty(newSelectedProperty);
                    }, 10);
                  }}
                />
              )) : (
                <Text size="sm" color="dimmed" mt={10} mb={15}>
                  {`No ${type === "filter-space" ? "space" : "member"} properties have been created`}
                </Text>
              )}
            </SortableContext>
          </DndContext>
        )}

        <Divider my="sm" />

        <FilterPropertySelector
          TargetComponent={({ onClick }) => (
            <Button
              variant="default"
              onClick={() => {
                if (canCreateFilters && !canCreateFilters[0]) {
                  setOpened([true, canCreateFilters[1], canCreateFilters[2]]);
                  return;
                }
                onClick();
              }}
              leftIcon={<FontAwesomeIcon icon={solid("plus")} color="grey" />}
              radius="md"
              size="xs"
              mt={10}
              mr={5}
              style={{ width: 130 }}
            >
              <Text color="grey" size="xs">Add property</Text>
            </Button>
          )}
          withinPortal={true}
          slug={slug}
          type={type}
          allFilters={allFilters}
          handlers={handlers}
          selectedProperty={selectedProperty}
          setSelectedProperty={setSelectedProperty}
        />
        <Box style={{ height: isComponent ? 10 : 100 }} />
      </ScrollArea>
      <PremiumModal opened={opened} setOpened={setOpened} />
    </>
  );
}
